import { fetchJSON as fetchAPI } from './client';

export type VoCDashboard = {
  tenant_id: string;
  total_reviews: number;
  avg_rating: number;
  positive_sentiment_pct: number;
  negative_sentiment_pct: number;
  active_issues: number;
  critical_issues: number;
  top_topics_count: number;
  avg_return_rate_pct: number;
  review_velocity_per_day: number;
  active_recommendations: number;
  customer_health_score: number;
};

export type ReviewRecord = {
  id: string;
  sku: string;
  asin?: string;
  product_name?: string;
  rating: number;
  title?: string;
  body?: string;
  sentiment: string;
  topics?: string[];
  verified: boolean;
  helpful_count?: number;
  review_date: string;
};

export type SentimentSummary = {
  id: string;
  sku?: string;
  category?: string;
  positive_pct: number;
  neutral_pct: number;
  negative_pct: number;
  overall_sentiment: string;
  trend_direction?: string;
  review_count: number;
  avg_rating: number;
};

export type TopicRecord = {
  id: string;
  name: string;
  category?: string;
  mention_count: number;
  sentiment: string;
  trend_pct?: number;
  sample_phrases?: string[];
};

export type IssueRecord = {
  id: string;
  type: string;
  title: string;
  description?: string;
  severity: string;
  sku?: string;
  occurrences: number;
  evidence?: string;
  status?: string;
};

export type ReturnIntelligence = {
  id: string;
  sku: string;
  category?: string;
  return_rate_pct: number;
  return_count: number;
  top_reason?: string;
  root_cause?: string;
  trend_direction?: string;
  recommendation?: string;
};

export type VocabularyInsight = {
  id: string;
  sku?: string;
  category?: string;
  top_words?: string[];
  search_phrases?: string[];
  buying_motivations?: string[];
  pain_points?: string[];
  positive_expressions?: string[];
  negative_expressions?: string[];
};

export type VoCRecommendation = {
  id: string;
  type: string;
  title: string;
  description?: string;
  evidence?: string;
  sku?: string;
  expected_impact?: string;
  confidence_score: number;
  priority_rank: number;
  requires_approval: boolean;
};

export type TimelineEntry = {
  id: string;
  event_type: string;
  title: string;
  summary?: string;
  sku?: string;
  severity?: string;
  created_at: string;
};

const API_BASE = import.meta.env.VITE_VOCINTEL_API ?? '/api/v1/vocintel';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const vocintelApi = {
  dashboard: () => fetchJSON<VoCDashboard>('/dashboard'),
  listReviews: () => fetchJSON<ReviewRecord[]>('/reviews'),
  listSentiment: () => fetchJSON<SentimentSummary[]>('/sentiment'),
  listTopics: () => fetchJSON<TopicRecord[]>('/topics'),
  listIssues: () => fetchJSON<IssueRecord[]>('/issues'),
  listReturns: () => fetchJSON<ReturnIntelligence[]>('/returns'),
  listVocabulary: () => fetchJSON<VocabularyInsight[]>('/vocabulary'),
  listInsights: () => fetchJSON<TimelineEntry[]>('/insights'),
  listRecommendations: () => fetchJSON<VoCRecommendation[]>('/recommendations'),
  generateRecommendations: () => fetchJSON<VoCRecommendation[]>('/recommendations/generate', { method: 'POST' }),
  approveRecommendation: (id: string) =>
    fetchJSON<VoCRecommendation>(`/recommendations/${id}/approve`, { method: 'POST', body: JSON.stringify({}) }),
  listActivity: () => fetchJSON<{ id: string; message: string }[]>('/activity'),
};
